import { Autocomplete, TextField, TextFieldProps } from '@mui/material';
import { useEffect, useState } from 'react';
import { usePage } from '@inertiajs/react';

interface PlaceType {
  description: string;
  place_id: string;
}

interface GooglePlacesAutocompleteProps {
  value: string;
  onChange: (value: string, placeId?: string) => void;
  textFieldProps?: Partial<TextFieldProps>;
}

interface PageProps {
  config?: {
    google_places_api_key?: string;
  };
  [key: string]: any;
}

const GooglePlacesAutocomplete = ({ value, onChange, textFieldProps }: GooglePlacesAutocompleteProps) => {
  const [places, setPlaces] = useState<PlaceType[]>([]);
  const [inputValue, setInputValue] = useState(value || '');
  const { props } = usePage<PageProps>();

  const fetchPlaces = async (input: string) => {
    if (!input) return;

    const apiKey = props.config?.google_places_api_key || 'AIzaSyDlBkUlEkh35iQx8z7C5bzUbU556yGg8qw'; // Fallback to hardcoded value if not in config

    try {
      const response = await fetch('https://places.googleapis.com/v1/places:autocomplete', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Goog-Api-Key': apiKey,
        },
        body: JSON.stringify({
          input: input
        })
      });

      const data = await response.json();

      if (data.suggestions) {
        const mappedPlaces = data.suggestions.map((suggestion: any) => ({
          description: suggestion.placePrediction.text.text,
          place_id: suggestion.placePrediction.placeId
        }));
        setPlaces(mappedPlaces);
      }
    } catch (error) {
      console.error('Error fetching places:', error);
    }
  };

  // Debounce the input to avoid too many API calls
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (inputValue) {
        fetchPlaces(inputValue);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [inputValue]);

  return (
    <Autocomplete
      freeSolo
      filterOptions={(x) => x} // Disable built-in filtering
      options={places}
      getOptionLabel={(option) =>
        typeof option === 'string' ? option : option.description
      }
      value={value}
      inputValue={inputValue}
      onChange={(event, newValue) => {

        // Handle different types of newValue
        if (typeof newValue === 'string') {
          // If it's just a string, we don't have a place_id
          onChange(newValue, undefined);
        } else if (newValue && 'place_id' in newValue) {
          // If it's an object with place_id, extract both description and place_id
          onChange(newValue.description, newValue.place_id);
        } else {
          // Fallback for null or other unexpected values
          onChange('', undefined);
        }
      }}
      onInputChange={(event, newValue) => {
        setInputValue(newValue);
        fetchPlaces(newValue);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          {...textFieldProps}
          fullWidth
          variant="outlined"
        />
      )}
      isOptionEqualToValue={(option, value) =>
        option.place_id === (typeof value === 'string' ? value : value.place_id)
      }
    />
  );
};

export default GooglePlacesAutocomplete;